import { Column, ColumnKeys, PhasrMonitoredRulesWebComponentSchema } from '../types';
import { DEFAULT_PAGE_SIZE, defaultPageSizeOptions, GridViewMetadata } from '../../../grid/gridView/types';
import { ColumnDetails, ColumnType, ColumnValue } from '../../../grid/types/common';
import { PhasrMonitoredRulesGridDataProvider } from '../dataProvider/PhasrMonitoredRulesGridDataProvider';
import { Filter } from '../../../grid/gridFilters/types';
import { translateFn } from '../../../utils/translator.helper';
import { getViewState } from '../../../utils/states.utils';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { API_TYPE_PHASR } from '../../../webcomponents.constants';
import { GetItemsResponse } from '../../../ui/gzSingleSelectComplex/types';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { getPhasrCategoryTypesName } from '../../../types/common/phasrTypes';
import { LabelWidthValues } from '../../../types/common/types';

export class GridConfig {
  private pageSizeOptions = defaultPageSizeOptions;
  private defaultCompactViewChecked = false;
  private defaultPageSize = DEFAULT_PAGE_SIZE;
  private dependencies: any;
  private defaultCompanyId: any;

  constructor() {}

  public async getDataForView(dependencies: any): Promise<PhasrMonitoredRulesWebComponentSchema> {
    this.dependencies = dependencies;

    await this.setDefaultCompanyId();

    const dataProvider = new PhasrMonitoredRulesGridDataProvider(dependencies, this.getColumnDetails());

    const gridState = getViewState(dataProvider.viewStateName);
    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'phasr-monitored-rules-grid-default-view',
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: (gridState.columns ?? this.getColumns()),
      filters: gridState.filters ?? this.getDefaultFilters(),
      pageSize: gridState.pageSize ?? 20,
      isCompactViewChecked: gridState.compactView ?? false,
      enableRowClickEvent: false,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      showGlobalActions: false,
      allColumnDetails: this.getColumnDetails(),
      pageNumber: 1,
      dataProvider: dataProvider,
      enableCSVExport: false,
      cellClassNameGenerator: this.getCellClassNameGenerator,
      columnConfigs: [],
      filtersUiConfigs: this.getFiltersUiConfigs(),
      sortColumnName: ColumnKeys.RULE_TRIGGER_DATE,
      sortDirection: 'desc',
    };

    return {
      viewMetaData,
      defaultViewProps: {
        displayedColumns: this.getColumns(),
        filters: this.getDefaultFilters(),
        pageSize: this.defaultPageSize,
        compactView: this.defaultCompactViewChecked,
      },
    };
  }

  private async setDefaultCompanyId() {
    try {
      this.defaultCompanyId = await this.dependencies.api.clients.Company.getRootOrFirstAvailableCompany({},
        getRequestDefaultOptions()
      );
    } catch (error) {
      this.defaultCompanyId = '';
    }
  }

  private getDefaultFilters(): Array<Filter<ColumnKeys>> {
    const defaultFilters: Array<Filter<ColumnKeys>> = [];

    if (this.dependencies.isPartner()) {
      defaultFilters.push({[ColumnKeys.COMPANY]: {
          exactMatch: this.defaultCompanyId,
        },
      });
    }

    defaultFilters.push({
      [ColumnKeys.RULE_NAME]: {
        complexEnumFilter: []
      }
    }, {
      [ColumnKeys.RULE_TRIGGER_DATE]: {
        timeRange: {
          kind: TimeRangeKind.ALL_TO_DATE
        }
      }
    }, {
      [ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES]: {
        complexEnumFilter: []
      }
    }, {
      [ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES]: {
        complexEnumFilter: []
      }
    });

    return defaultFilters;
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    const predefinedFiltersKeys: Array<ColumnKeys> = [];

    if (this.dependencies.isPartner()) {
      predefinedFiltersKeys.push(ColumnKeys.COMPANY);
    }

    predefinedFiltersKeys.push(ColumnKeys.RULE_NAME);
    predefinedFiltersKeys.push(ColumnKeys.RULE_TRIGGER_DATE);
    predefinedFiltersKeys.push(ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES);
    predefinedFiltersKeys.push(ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES);

    return predefinedFiltersKeys;
  }

  private getFiltersUiConfigs() {
    return [
      this.getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      this.getCreatedOnFilterUiConfig(ColumnKeys.RULE_TRIGGER_DATE),
      this.getBehavioralProfilesFilterUiConfig(ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES),
      this.getBehavioralProfilesFilterUiConfig(ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES),
    ];
  }

  private getBehavioralProfilesFilterUiConfig(columnKey: ColumnKeys) {
    return {
      key: columnKey,
      config: {
        labelWidth: LabelWidthValues.LARGE
      }
    }
  }

  private getCreatedOnFilterUiConfig(columnKey: ColumnKeys) {
    return {
      key: columnKey,
      config: {
        activeRange: 365,
        enableSelectAllToDate: true,
        additionalTimeRangeOptions: [
          {
            label: 'Last 90 days',
            value: 90,
          }
        ],
      },
    }
  }

  private getCompanyFilterUiConfig(columnKey: ColumnKeys) {
    return {
      key: columnKey,
      config: {
        apiType: API_TYPE_PHASR,
        populateFilterCallback(filterComponent, value) {
          const selectedValues: GetItemsResponse = {
            items: [],
            totalItemsCount: 0,
          };

          filterComponent.setFilterOptions(selectedValues).then(() => {
            const itemFound = filterComponent.filterOptions.find(option => option.value === value.exactMatch);
            filterComponent.setFormValue(itemFound ? itemFound.value : null);
          });
        },
      },
    };
  }

  private getColumns(): Array<Column> {
    const columns: Array<Column> = [
      Column.RULE_NAME,
      Column.RECOMMENDATIONS,
      Column.RULE_TRIGGER_DATE,
      Column.TARGETED_ACTIVITY_TYPE,
      Column.RULE_TRIGGERS,
      Column.BEHAVIORAL_PROFILES,
      Column.RESTRICTED_BEHAVIORAL_PROFILES,
    ];

    if (this.dependencies.isPartner()) {
      columns.push(Column.COMPANY);
    }

    columns.push(Column.ROW_ACTIONS);

    return columns;
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    let allColumns: Array<ColumnDetails<Column, ColumnKeys>>;

    allColumns = [
      {
        column: Column.RULE_NAME,
        key: ColumnKeys.RULE_NAME,
        columnName: translateFn('Rule name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        filteringDisabled: false,
        flexGrow: 1,
        width: '172px', //12px is the padding
        remoteDetails: {
          type: RemoteDetailsTypes.PhasrMonitoredRuleName,
        },
      },
      {
        column: Column.RECOMMENDATIONS,
        key: ColumnKeys.RECOMMENDATIONS,
        columnName: translateFn('Recommendations'),
        type: ColumnType.NUMBER,
        removable: false,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
        width: '160px',
      },
      {
        column: Column.RULE_TRIGGER_DATE,
        key: ColumnKeys.RULE_TRIGGER_DATE,
        columnName: translateFn('Rule trigger date'),
        type: ColumnType.DATE,
        removable: false,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 1,
        width: '160px',
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.TARGETED_ACTIVITY_TYPE,
        key: ColumnKeys.TARGETED_ACTIVITY_TYPE,
        columnName: translateFn('Targeted activity type'),
        type: ColumnType.ENUM,
        values: this.getCategoryTypesNames(),
        removable: true,
        sortable: false,
        filteringDisabled: false,
        flexGrow: 1,
        width: '160px',
      },
      {
        column: Column.RULE_TRIGGERS,
        key: ColumnKeys.RULE_TRIGGERS,
        columnName: translateFn('Rule triggers'),
        type: ColumnType.NUMBER,
        removable: true,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
        width: '160px',
      },
      {
        column: Column.BEHAVIORAL_PROFILES,
        key: ColumnKeys.BEHAVIORAL_PROFILES,
        columnName: translateFn('Behavioral profiles'),
        type: ColumnType.NUMBER,
        removable: true,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
        width: '160px',
      },
      {
        column: Column.BEHAVIORAL_PROFILE_IDENTITIES,
        key: ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES,
        columnName: translateFn('Behavioral profile identities'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        flexGrow: 1,
        isHiddenInGridSettings: true,
        filteringDisabled: false,
        remoteDetails: {
          type: RemoteDetailsTypes.PhasrBehavioralProfileIdentitiesForRules,
        },
      },
      {
        column: Column.BEHAVIORAL_PROFILE_RESOURCES,
        key: ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES,
        columnName: translateFn('Behavioral profile resources'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        flexGrow: 1,
        isHiddenInGridSettings: true,
        filteringDisabled: false,
        remoteDetails: {
          type: RemoteDetailsTypes.PhasrBehavioralProfileResourcesForRules,
        },
      },
      {
        column: Column.RESTRICTED_BEHAVIORAL_PROFILES,
        key: ColumnKeys.RESTRICTED_BEHAVIORAL_PROFILES,
        columnName: translateFn('Restricted behavioral profiles'),
        type: ColumnType.NUMBER,
        removable: true,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
        width: '240px',
      },
    ];

    if (this.dependencies.isPartner()) {
      allColumns.push({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: this.defaultCompanyId,
        },
        width: '160px',
      });
    }

    allColumns.push({
      column: Column.ROW_ACTIONS,
      key: ColumnKeys.ROW_ACTIONS,
      columnName: '',
      type: ColumnType.ACTION,
      removable: false,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    });

    return allColumns;
  }

  private getCellClassNameGenerator(_, rowData: any): string {
    const {
      item: { isGhost },
    } = rowData;

    return isGhost ? 'cell-ghost' : '';
  }

  private getCategoryTypesNames(): ColumnValue[] {
    return getPhasrCategoryTypesName();
  }
}
